import {UTCDate} from "@date-fns/utc";
import {differenceInSeconds} from "date-fns";
import {Link} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {CheckTypeDisplay, MonitoringServiceType, Service} from "@/lib/types/checks.ts";
import {isCheckPending} from "@/pages/checks/analysis/utils.ts";

interface CheckHeaderProps {
  service: Service;
  start: Date;
  end: Date;
  fixedRange?: StandardDateRange;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
}

export const CheckHeader = ({service, end, fixedRange, onRangeChange, start}: CheckHeaderProps) => {
  const {user} = useCurrentUserContext();

  let status = PillStatus.UP;

  if (!service.supports_downtime_alerting) {
    status = PillStatus.SILENCED;
  } else if (service.is_paused) {
    status = PillStatus.PAUSED;
  } else if (service.is_under_maintenance) {
    status = PillStatus.IN_MAINTENANCE;
  } else if (isCheckPending(service.cached_state_is_up, differenceInSeconds(new UTCDate(), service.created_at))) {
    status = PillStatus.PENDING;
  } else if (!service.cached_state_is_up) {
    status = PillStatus.DOWN;
  }

  const showAddress = service.device.address.toLowerCase() !== service.monitoring_service_type.toLowerCase();

  return (
    <div className="row">
      <div className="col-md-6">
        <div data-testid="check-name" className="pulseTextH3Bold text-break">
          {service.name}
        </div>

        <div className="d-flex align-items-center gap-3 mt-1 pulseTextBodySmall text-gray-700">
          <span className="text-nowrap">{CheckTypeDisplay[service.monitoring_service_type]}</span>
          {showAddress && (
            <>
              <Divider orientation="vertical" />
              <Tooltip tooltipContent={service.msp_address} delayMs={500}>
                <span className="ellipsis-overflow">{service.msp_address}</span>
              </Tooltip>
            </>
          )}
          <Divider orientation="vertical" />
          <StatusPill status={status} variant="textAndIcon" type="incident" />
        </div>
      </div>

      <div className="col-md-6 d-flex gap-3 align-items-center justify-content-end">
        <DatePicker
          mode={"range"}
          selected={{from: start, to: end}}
          fixedRange={fixedRange}
          timezone={user!.account!.timezone}
          dateFormat={user!.current_date_format}
          placement={"bottom-end"}
          onChange={(date, fixedRange) => {
            onRangeChange(date?.from ?? null, date?.to ?? null, fixedRange ? fixedRange : null);
          }}
          disableFutureDates
          hasPopoverInput
          inputProps={{
            size: "large",
          }}
        />

        {service.monitoring_service_type !== MonitoringServiceType.NETWORK && (
          <Button
            variant="secondary"
            size="large"
            as={Link}
            to={
              service.monitoring_service_type === MonitoringServiceType.GROUP
                ? `/checks/group/${service.id}`
                : `/checks/${service.id}/analysis`
            }
            className="text-nowrap"
          >
            Real-Time Analysis
          </Button>
        )}
      </div>
    </div>
  );
};
